import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  Breadcrumbs,
  Chip,
  TextField,
  IconButton,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import { formatPath } from '../../utils/formatUtils';
import videoCrudServiceInstance from '../../services/VideoCRUDService';

const VideoBrowserDialog = ({ 
  open, 
  onClose, 
  onSelect, 
  currentPath = '/', 
  title = "Select Video",
  multiSelect = false, // New prop to enable multiple selection
  calledFrom = null,    // New prop to identify caller component
  folderSelectOnly = false, // New prop to restrict selection to folders
  showFolderSelectionButton = false // New prop to show/hide folder selection button
}) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [browsePath, setBrowsePath] = useState('/');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState([]); // New state for multiple selections
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Home', path: '/' }]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      // Initialize path to current path when dialog opens
      setBrowsePath(currentPath || '/');
      setSelectedVideo(null);
      setSelectedVideos([]); // Clear selected videos array
      loadFolderContents(currentPath || '/');
    }
  }, [open, currentPath]);

  const loadFolderContents = async (path) => {
    setError(null);
    setLoading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('authUser'));
      if (!userData) {
        setError('User data not found');
        setLoading(false);
        return;
      }

      const response = await fileSystemAccessServiceInstance.getFolderContents(
        path, 
        userData.email, 
        userData.access
      );

      // Filter to separate folders and videos
      const folderItems = (response.data || [])
        .filter(item => item && item.isFolder === true)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      const videoItems = (response.data || [])
        .filter(item => item && (item.isFolder === false))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      if (videoItems.length > 0) {
        // Create an array of promises for all video requests
        const videoPromises = videoItems.map(async (video) => {
          try {
            // Get video details individually using the video ID
            const videoResponse = await videoCrudServiceInstance.getVideoById(video.data);
            
            // If successful, update the video object with additional details
            if (videoResponse && videoResponse.data) {
              return {
                ...video,
                name: videoResponse.data.videoName || video.name,
                lastModified: videoResponse.data.lastModifiedDate || video.lastModified,
                lastModifiedBy: videoResponse.data.updatedBy || video.lastModifiedBy,
                duration: videoResponse.data.duration,
                languages: videoResponse.data.videoLanguages,
                videoId: videoResponse.data.id,
                // Include any other properties you need
              };
            }
            return video; // Return original if fetch failed
          } catch (err) {
            console.error(`Error fetching video details for ID ${video.data}:`, err);
            return video; // Return original video object on error
          }
        });
        
        // Wait for all video fetch operations to complete
        const enhancedVideos = await Promise.allSettled(videoPromises);
        
        // Process results, using the original item if a promise was rejected
        const processedVideos = enhancedVideos.map((result, index) => {
          if (result.status === 'fulfilled') {
            return result.value;
          } else {
            console.error(`Failed to fetch details for video:`, result.reason);
            return videoItems[index]; // Use original data
          }
        });
        console.log('Processed videos:', processedVideos);
        setVideos(processedVideos);
      } else {
        setVideos([]);
      }
      
      setFolders(folderItems);
      updateBreadcrumbs(path);
      
    } catch (err) {
      console.error('Error loading folder contents:', err);
      setError('Failed to load folder contents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBreadcrumbs = (path) => {
    const parts = path.split('/').filter(Boolean);
    const crumbs = [{ name: 'Home', path: '/' }];
    
    let currentPath = '';
    for (const part of parts) {
      currentPath += '/' + part;
      crumbs.push({
        name: part,
        path: currentPath
      });
    }
    
    setBreadcrumbs(crumbs);
  };

  const handleFolderClick = (folder) => {
    // Don't clear selected videos in multi-select mode
    if (!multiSelect) {
      setSelectedVideo(null);
    }
    
    const folderPath = folder.path || `${browsePath === '/' ? '' : browsePath}/${folder.name}`;
    setBrowsePath(folderPath);
    loadFolderContents(folderPath);
  };

  const handleBreadcrumbClick = (path) => {
    // Don't clear selected videos in multi-select mode
    if (!multiSelect) {
      setSelectedVideo(null);
    }
    
    setBrowsePath(path);
    loadFolderContents(path);
  };

  const handleSelectCurrentFolder = () => {
    console.log('Select current folder clicked, path:', browsePath);
    if (onSelect) {
      const folderData = {
        path: browsePath,
        name: getFolderNameFromPath(browsePath),
        isFolder: true
      };
      console.log('Sending folder data to parent:', folderData);
      onSelect(folderData);
    }
  };

  // Helper function to get folder name from path
  const getFolderNameFromPath = (path) => {
    if (!path || path === '/') return 'Root';
    const parts = path.split('/').filter(p => p);
    return parts.length > 0 ? parts[parts.length - 1] : 'Root';
  };

  const handleGoBack = () => {
    if (browsePath === '/') return;
    
    // Don't clear selected videos in multi-select mode
    if (!multiSelect) {
      setSelectedVideo(null);
    }
    
    const pathParts = browsePath.split('/').filter(Boolean);
    pathParts.pop();
    const parentPath = pathParts.length ? '/' + pathParts.join('/') : '/';
    
    setBrowsePath(parentPath);
    loadFolderContents(parentPath);
  };

  const handleSelectVideo = (video) => {
    if (multiSelect) {
      // In multi-select mode, toggle selection in the array
      setSelectedVideos(prev => {
        const videoExists = prev.some(v => v.data === video.data);
        
        if (videoExists) {
          // Remove if already selected
          return prev.filter(v => v.data !== video.data);
        } else {
          // Add if not already selected
          return [...prev, video];
        }
      });
    } else {
      // In single-select mode, just update the selected video
      setSelectedVideo(video);
    }
  };

  const handleConfirm = () => {
    if (multiSelect && selectedVideos.length > 0) {
      // For multi-select, return all selected videos with path information
      const videosWithPath = selectedVideos.map(video => ({
        ...video,
        folderPath: browsePath,
        formattedPath: formatPath(browsePath)
      }));
      onSelect(videosWithPath);
      onClose();
    } else if (!multiSelect && selectedVideo) {
      // For single-select, return the selected video with path information
      const videoWithPath = {
        ...selectedVideo,
        folderPath: browsePath,
        formattedPath: formatPath(browsePath)
      };
      onSelect(videoWithPath);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {title}
          {multiSelect && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (Select multiple videos)
            </Typography>
          )}
        </Typography>
        
        {/* 7. Show selected videos count in multi-select mode */}
        {multiSelect && selectedVideos.length > 0 && (
          <Chip 
            label={`${selectedVideos.length} selected`} 
            color="primary" 
            onDelete={() => setSelectedVideos([])}
            icon={<VideoLibraryIcon />}
            sx={{ ml: 1 }}
          />
        )}
        
        {/* Show single selection in single-select mode */}
        {!multiSelect && selectedVideo && (
          <Chip 
            label={selectedVideo.name} 
            color="primary" 
            onDelete={() => setSelectedVideo(null)}
            icon={<VideoLibraryIcon />}
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      
      <Box sx={{ px: 3, py: 1 }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="folder navigation"
          sx={{ mb: 1, flexWrap: 'wrap' }}
        >
          {breadcrumbs.map((crumb, index) => (
            <Button
              key={crumb.path}
              color={browsePath === crumb.path ? "primary" : "inherit"}
              variant={browsePath === crumb.path ? "text" : "text"}
              onClick={() => handleBreadcrumbClick(crumb.path)}
              size="small"
              startIcon={index === 0 ? <HomeIcon fontSize="small" /> : null}
              sx={{ 
                textTransform: 'none',
                fontWeight: browsePath === crumb.path ? 600 : 400
              }}
            >
              {crumb.name}
            </Button>
          ))}
        </Breadcrumbs>
      </Box>
      
      <Divider />
      
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="outlined" 
              onClick={() => loadFolderContents(browsePath)} 
              sx={{ mt: 1 }}
            >
              Try Again
            </Button>
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ py: 0 }}>
            {/* Back button for non-root folders */}
            {browsePath !== '/' && (
              <ListItemButton onClick={handleGoBack} divider>
                <ListItemIcon>
                  <ArrowBackIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary="Go back to parent folder" 
                />
              </ListItemButton>
            )}
            
            {/* Folder list */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Folders
            </Typography>
            
            {folders.length === 0 ? (
              <Box sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1
              }}>
                <Typography color="text.secondary">
                  No subfolders found in this location
                </Typography>
              </Box>
            ) : (
              <List sx={{ mb: 2 }}>
                {folders.map((folder) => (
                  <ListItemButton 
                    key={folder.path || folder.name} 
                    onClick={() => handleFolderClick(folder)}
                    divider
                  >
                    <ListItemIcon>
                      <FolderIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={folder.name} 
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Video list */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Videos
            </Typography>
            
            {videos.length === 0 ? (
              <Box sx={{ 
                p: 2, 
                textAlign: 'center',
                borderRadius: 1
              }}>
                <Typography color="text.secondary">
                  No videos found in this location
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} key={video._id || video.data}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        borderColor: 
                          multiSelect
                            ? selectedVideos.some(v => v.data === video.data)
                              ? 'primary.main' 
                              : 'divider'
                            : selectedVideo && selectedVideo.data === video.data
                              ? 'primary.main' 
                              : 'divider',
                        bgcolor: 
                          multiSelect
                            ? selectedVideos.some(v => v.data === video.data)
                              ? 'action.selected' 
                              : 'background.paper'
                            : selectedVideo && selectedVideo.data === video.data
                              ? 'action.selected' 
                              : 'background.paper',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleSelectVideo(video)}
                    >
                      {/* Show checkmark for selected videos */}
                      {(multiSelect 
                        ? selectedVideos.some(v => v.data === video.data)
                        : selectedVideo && selectedVideo.data === video.data
                      ) && (
                        <Tooltip title="Selected">
                          <CheckCircleIcon 
                            color="primary" 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              zIndex: 1
                            }} 
                          />
                        </Tooltip>
                      )}
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <VideoLibraryIcon 
                            color="secondary" 
                            sx={{ mt: 0.5, mr: 1 }} 
                          />
                          <Box>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                wordBreak: 'break-word',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {video.name}
                            </Typography>
                            
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              Modified: {new Date(video.lastModified || video.updatedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      {/* 9. Update the dialog actions */}
      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
        >
          Cancel
        </Button>
        
        <Box>
          {multiSelect && (
            <Button
              onClick={() => setSelectedVideos([])}
              disabled={selectedVideos.length === 0}
              sx={{ mr: 1 }}
            >
              Clear Selection
            </Button>
          )}
          
          {/* Add folder selection button conditionally */}
          {folderSelectOnly && showFolderSelectionButton && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSelectCurrentFolder}
              sx={{ mr: 1 }}
            >
              Select Current Folder
            </Button>
          )}
          
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            color="primary"
            disabled={multiSelect 
              ? selectedVideos.length === 0 
              : !selectedVideo
            }
            startIcon={<VideoLibraryIcon />}
          >
            {multiSelect 
              ? `Select ${selectedVideos.length} Video${selectedVideos.length !== 1 ? 's' : ''}` 
              : 'Select Video'
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VideoBrowserDialog;